import { Schema } from "@effect/schema";
import { Console, Context, Effect, Layer, pipe, Ref } from "effect";
import { isNumber } from "effect/Predicate";
import produce from "immer";

export type RpcMetrics = typeof RpcMetrics.Type;
export type RpcMetricsEncoded = typeof RpcMetrics.Encoded;

export const RpcMetrics = Schema.Record({
  key: Schema.String,
  value: Schema.Struct({
    fetchRequests: Schema.Number,
    rpcRequests: Schema.Record({
      key: Schema.String,
      value: Schema.Number,
    }),
  }),
}).annotations({ identifier: "RpcMetrics" });

export type RpcRequest = typeof RpcRequest.Type;
export type RpcRequestEncoded = typeof RpcRequest.Encoded;

export const RpcRequest = Schema.Struct({
  id: Schema.Number,
  method: Schema.String,
}).annotations({ identifier: "RpcRequest" });

export class RpcMetricsService extends Context.Tag("RpcMetricsService")<
  RpcMetricsService,
  Ref.Ref<RpcMetrics>
>() {}

export const BrowserRpcMetricsServiceLive = Layer.effect(
  RpcMetricsService,
  Ref.make({})
);

/**
 * Scoped layer for scripts and the sandbox: accumulates metrics in memory and,
 * when the scope closes, logs the RPC metrics table to the console. It does
 * NOT write to Redis, so local dev runs never pollute the durable metrics.
 */
export const ScriptRpcMetricsServiceLive = Layer.scoped(
  RpcMetricsService,
  Effect.gen(function* () {
    const acquire = Ref.make({});

    const release = getRpcStats;

    return yield* Effect.acquireRelease(acquire, release);
  })
);

export type UpdateRpcMetrics = ReturnType<typeof updateRpcMetrics>;
export type UpdateRpcMetricsSuccess = Effect.Effect.Success<UpdateRpcMetrics>;
export type UpdateRpcMetricsError = Effect.Effect.Error<UpdateRpcMetrics>;
export type UpdateRpcMetricsContext = Effect.Effect.Context<UpdateRpcMetrics>;

export function updateRpcMetrics(
  request: Request,
  rpcMetricsRef: Ref.Ref<RpcMetrics>
) {
  return Effect.gen(function* () {
    const domain = new URL(request.url).hostname;
    const body = yield* pipe(
      Effect.promise(() => request.json()),
      Effect.andThen(
        Schema.decode(Schema.Union(RpcRequest, Schema.Array(RpcRequest)))
      )
    );

    /**
     * For some reason, TS struggles with array narrowing here
     */
    const rpcRequestsArray = (
      Array.isArray(body) ? body : [body]
    ) as RpcRequest[];

    yield* Ref.update(rpcMetricsRef, (state) => {
      const newState = produce(state, (draft) => {
        if (!(domain in draft)) {
          draft[domain] = { fetchRequests: 0, rpcRequests: {} };
        }

        draft[domain].fetchRequests += 1;

        for (const { method } of rpcRequestsArray) {
          if (!(method in draft[domain].rpcRequests)) {
            draft[domain].rpcRequests[method] = 0;
          }

          draft[domain].rpcRequests[method] += 1;
        }
      });

      return newState;
    });
  });
}

type TableObj = {
  domain: string;
  fetchRequests: number;
  rpcRequests: number;
  [key: string]: string | number | undefined;
};

interface RpcStats {
  rpcBatches: number;
  rpcRequests: number;
}

export type GetRpcStats = ReturnType<typeof getRpcStats>;
export type GetRpcStatsSuccess = Effect.Effect.Success<GetRpcStats>;
export type GetRpcStatsError = Effect.Effect.Error<GetRpcStats>;
export type GetRpcStatsContext = Effect.Effect.Context<GetRpcStats>;

/**
 * Aggregates the in-memory per-domain metrics, logs them as a table to the
 * console, and returns the totals so callers can persist them.
 */
export function getRpcStats(
  rpcMetricsRef: Ref.Ref<RpcMetrics>
): Effect.Effect<RpcStats, never, never> {
  return Effect.gen(function* () {
    const rpcMetrics = yield* Ref.get(rpcMetricsRef);

    const totals: TableObj = {
      domain: "totals",
      fetchRequests: 0,
      rpcRequests: 0,
    };

    const table = Object.entries(rpcMetrics).map(
      ([domain, { fetchRequests, rpcRequests }]) => {
        let totalRpcRequests = 0;

        for (const [, count] of Object.entries(rpcRequests)) {
          totalRpcRequests += count;
        }

        const tableObj: TableObj = {
          domain,
          fetchRequests,
          rpcRequests: totalRpcRequests,
        };

        for (const [method, count] of Object.entries(rpcRequests)) {
          tableObj[method] = count;

          if (!(method in totals)) {
            totals[method] = 0;
          }

          const totalForMethod = totals[method];

          if (isNumber(totalForMethod)) {
            totals[method] = totalForMethod + count;
          }
        }

        totals.fetchRequests += fetchRequests;
        totals.rpcRequests += totalRpcRequests;

        return tableObj;
      }
    );

    table.push(totals);

    yield* Effect.log("RPC metrics:");
    yield* Console.table(table);

    const stats: RpcStats = {
      rpcBatches: totals.fetchRequests,
      rpcRequests: totals.rpcRequests,
    };

    return stats;
  });
}
